import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import DramaCard from "@/components/base/DramaCard";
import { useSeo } from "@/hooks/useSeo";
import { episodes } from "@/mocks/episodes";
import { relatedDramas } from "@/mocks/related";
import VideoPlayer from "./components/VideoPlayer";
import EpisodeSelector from "./components/EpisodeSelector";

export default function PlayPage() {
  const { episodeId } = useParams();

  const episode = useMemo(
    () => episodes.find((ep) => String(ep.number) === String(episodeId)),
    [episodeId]
  );

  useSeo({
    title: episode
      ? `善良的小姨子-第${episode.number}集在线播放`
      : "善良的小姨子-剧集播放",
    description: episode
      ? `善良的小姨子第${episode.number}集《${episode.title}》在线播放，${episode.summary.slice(0, 40)}...`
      : "善良的小姨子剧集在线播放页面。",
    keywords: "善良的小姨子在线观看,善良的小姨子第" + (episode ? episode.number : "") + "集,电视剧在线观看",
  });

  return (
    <SiteLayout>
      {!episode ? (
        <section className="w-full px-4 md:px-8 py-20 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </div>
          <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-5">此集内容暂不可用</h1>
          <p className="text-sm text-foreground-500 mt-2 max-w-md">
            抱歉，你访问的集数不存在或暂未上线。可以返回首页，或从下方选择其他集数继续观看。
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i> 返回首页
            </Link>
            <Link
              to="/play/1"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-100 border border-background-300 text-foreground-800 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-line"></i> 从第1集开始
            </Link>
          </div>
        </section>
      ) : (
        <>
          <section className="w-full px-4 md:px-8 pt-6 md:pt-8">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-4">
              <Link to="/" className="hover:text-primary-600 cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">第{episode.number}集在线播放</span>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 md:gap-8">
              <div>
                <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  {episode.title}
                  <span className="ml-2 text-sm font-normal text-foreground-500">
                    第 {episode.number} 集 / 共 {episodes.length} 集
                  </span>
                </h1>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-foreground-500 mt-2 mb-4">
                  <span>上线时间：{episode.releaseDate}</span>
                  <span>时长：{episode.duration}</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i> {episode.views} 次播放
                  </span>
                  <span className="px-2 py-0.5 rounded bg-secondary-100 text-secondary-900">高清</span>
                </div>

                <VideoPlayer poster={episode.thumbnail} title={episode.title} episodeNumber={episode.number} />

                <div className="mt-6 rounded-lg bg-background-50 border border-background-200 p-4 md:p-5">
                  <h2 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-3 flex items-center gap-2">
                    <i className="ri-file-text-line text-primary-500"></i>
                    本集剧情
                  </h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{episode.summary}</p>
                </div>

                <div className="mt-6">
                  <EpisodeSelector current={episode.number} />
                </div>
              </div>

              <aside className="lg:sticky lg:top-20 h-fit">
                <div className="rounded-lg bg-primary-50 border border-primary-100 p-4 md:p-5">
                  <h3 className="font-heading text-base font-bold text-primary-900 flex items-center gap-2">
                    <i className="ri-information-line"></i> 观看说明
                  </h3>
                  <ul className="mt-3 space-y-2.5 text-xs text-primary-800 leading-relaxed">
                    <li className="flex gap-2">
                      <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                      播放器为前端模拟演示，非真实视频源。
                    </li>
                    <li className="flex gap-2">
                      <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                      每周三、周五 20:00 更新 2 集。
                    </li>
                    <li className="flex gap-2">
                      <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                      可在下方相关推荐中继续探索相似剧集。
                    </li>
                  </ul>
                </div>

                <div className="mt-6">
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-3 flex items-center gap-2">
                    <i className="ri-movie-2-line text-accent-500"></i> 相关推荐
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {relatedDramas.slice(0, 4).map((item) => (
                      <DramaCard
                        key={item.id}
                        to="/related"
                        cover={item.cover}
                        title={item.title}
                        genre={item.genre}
                        rating={item.rating}
                      />
                    ))}
                  </div>
                  <Link
                    to="/related"
                    className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md bg-background-100 border border-background-300 text-foreground-800 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    查看更多推荐 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </aside>
            </div>
          </section>
        </>
      )}
    </SiteLayout>
  );
}